import { ChevronLeft, ChevronRight, Menu as MenuIcon, Presentation } from 'lucide-react';
import { Link, useMatch } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Me } from '../auth/useMe';
import { BrandMark } from '../components/BrandMark';
import { PrintMenu } from './PrintMenu';
import { SaveStatus } from './SaveStatus';
import { currentWeek, parseIsoWeek, sameWeek, shiftWeek, weekRangeLabel } from './weekMath';
import type { IsoWeek } from './weekMath';

type ToolbarProps = {
  classroomName: string;
  me: Me | null | undefined;
  logoSrc: string | null;
  onLogout: () => void;
};

/** Menu rows are touch targets: 44 px tall. */
const ITEM = 'min-h-11';

const weekPath = (week: IsoWeek) => `/week/${week.year}/${week.week}`;

/** Which week (and day) the URL shows; null off the week routes. */
function useShownWeek(): { week: IsoWeek; boardPath: string } | null {
  const dayMatch = useMatch('/week/:jaar/:week/dag/:dag/*');
  const weekMatch = useMatch('/week/:jaar/:week/*');
  const params = dayMatch?.params ?? weekMatch?.params;
  const week = params ? parseIsoWeek(params) : null;
  if (!week) return null;
  const base = weekPath(week);
  return { week, boardPath: dayMatch ? `${base}/dag/${dayMatch.params.dag}/digibord` : `${base}/digibord` };
}

/** The bar above every signed-in page: brand, week navigation, board, print, and the menu. */
export function Toolbar({ classroomName, me, logoSrc, onLogout }: ToolbarProps) {
  const shown = useShownWeek();
  const now = currentWeek();

  return (
    <header aria-label="Werkbalk" className="panel no-print flex flex-wrap items-center gap-2 px-3 py-2 sm:px-4">
      <Link to="/" className="mr-2 flex min-h-11 items-center gap-3" aria-label={`${classroomName}, naar deze week`}>
        {logoSrc && <img src={logoSrc} alt="" className="h-9 w-auto rounded-md" />}
        <BrandMark name={classroomName} />
      </Link>

      {shown && (
        <nav aria-label="Week kiezen" className="flex items-center gap-1">
          <Button asChild variant="ghost" size="icon" className="min-h-11 min-w-11">
            <Link to={weekPath(shiftWeek(shown.week, -1))} aria-label="Vorige week">
              <ChevronLeft />
            </Link>
          </Button>
          <span className="px-1 text-base font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
            Week {shown.week.week} <span className="font-normal opacity-70">· {weekRangeLabel(shown.week)}</span>
          </span>
          <Button asChild variant="ghost" size="icon" className="min-h-11 min-w-11">
            <Link to={weekPath(shiftWeek(shown.week, 1))} aria-label="Volgende week">
              <ChevronRight />
            </Link>
          </Button>
          {!sameWeek(shown.week, now) && (
            <Button asChild variant="outline" className="min-h-11">
              <Link to={weekPath(now)}>Deze week</Link>
            </Button>
          )}
        </nav>
      )}
      {shown && <SaveStatus year={shown.week.year} week={shown.week.week} />}

      <div className="ml-auto flex items-center gap-2">
        {shown && (
          <>
            <Button asChild variant="outline" className="min-h-11">
              <Link to={shown.boardPath}>
                <Presentation />
                Op het bord
              </Link>
            </Button>
            <PrintMenu />
          </>
        )}

        <nav aria-label="Hoofdmenu">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-11 min-w-11" aria-label="Menu">
                <MenuIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
              <DropdownMenuItem asChild className={ITEM}><Link to="/rooster">Rooster</Link></DropdownMenuItem>
              <DropdownMenuItem asChild className={ITEM}><Link to="/instellingen/vakken">Vakken</Link></DropdownMenuItem>
              <DropdownMenuItem asChild className={ITEM}><Link to="/instellingen/tijdvakken">Tijdvakken</Link></DropdownMenuItem>
              <DropdownMenuItem asChild className={ITEM}><Link to="/instellingen/thema">Uiterlijk</Link></DropdownMenuItem>
              {me?.admin && <DropdownMenuItem asChild className={ITEM}><Link to="/toegang">Toegang</Link></DropdownMenuItem>}
              <DropdownMenuSeparator />
              {me?.displayName && <DropdownMenuLabel className="font-normal opacity-70">{me.displayName}</DropdownMenuLabel>}
              <DropdownMenuItem className={ITEM} onSelect={onLogout}>Uitloggen</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </header>
  );
}
